import React from "react";
import { Link } from "react-router-dom";
import { Dumbbell, FlaskConical, Activity, HeartPulse, ShieldCheck, CheckCircle2, ArrowRight, CalendarDays, MessageCircle } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema, speakableSchema } from "../components/Seo";
import { Reveal, Eyebrow, BtnGhost, FaqList, Crumbs } from "../components/Shared";
import { LeadBand } from "../components/LeadForm";
import { QuickAnswer } from "../components/QuickAnswer";
import { IMG, SITE } from "../data/mock";

const QUICK_ANSWER =
  "Boston Iron Grit in West Roxbury offers access to peptide and GLP-1 programs through licensed medical partners, paired with coached strength training, lab work and InBody body-composition tracking. A licensed provider runs the consultation and decides eligibility; our coaches build the training, nutrition habits and accountability around it. Start with a free 20-minute goal review.";

const PEPTIDE_FAQS = [
  { q: "Does Boston Iron Grit offer peptides in Boston?", a: "Yes, through licensed medical partners. After a provider consultation and eligibility screening, members can access peptide and GLP-1 programs at member pricing, paired with coached training at our West Roxbury gym." },
  { q: "Do I need to be a member?", a: "No. Live B.I.G. 365 is available to non-members for $100/month and is included free with the Platinum Membership ($275/month)." },
  { q: "Who decides if a peptide or GLP-1 is right for me?", a: "A licensed medical provider, after reviewing your history and labs. Our coaches don't prescribe or recommend specific medications." },
  { q: "Why pair GLP-1s with strength training?", a: "Resistance training and adequate protein help preserve lean muscle during weight loss. We track this with InBody scans so you can see muscle vs. fat changes." },
  { q: "Are labs required?", a: "A baseline is strongly recommended and usually required by the provider. Order Men's or Women's Longevity + Performance Labs through ChalkIt Pro at member pricing." },
  { q: "How much does it cost?", a: "Coaching and program access come through Live B.I.G. 365 or Gold Standard Programs. Provider visits, labs and any prescriptions are billed separately by the licensed partner." },
  { q: "Is this safe for people over 50?", a: "Many of our members are 50+. Eligibility is decided by the provider; training is scaled to every member's level." },
  { q: "Where are you located?", a: "1230 VFW Parkway, West Roxbury, MA 02132, with free parking, minutes from Dedham, Roslindale, Needham, Newton, Brookline, Jamaica Plain and Hyde Park." },
];

const STEPS = [
  { t: "Free goal review", d: "A 20-minute conversation about goals, history and health priorities." },
  { t: "Baseline", d: "InBody scan plus Men's or Women's Longevity + Performance Labs." },
  { t: "Provider consultation", d: "A licensed medical partner reviews your labs and history and determines eligibility." },
  { t: "Coached training + habits", d: "Your program, nutrition habits and check-ins with your coach." },
  { t: "Retest and adjust", d: "Quarterly labs and scans measure what changed." },
];

const WHO = [
  { icon: HeartPulse, t: "Adults 35+ focused on longevity", d: "Training and habits built for the next 30 years, guided by your own lab data." },
  { icon: Activity, t: "Lose fat without losing muscle", d: "Strength work and protein coaching protect lean mass while the scale moves." },
  { icon: Dumbbell, t: "Athletes managing recovery", d: "Provider-guided options reviewed alongside your training load and labs." },
  { icon: FlaskConical, t: "Members 50+ who want independence", d: "Strength, bone and balance for decades — every session scales to your level." },
];

const EligibilityNote = ({ className = "" }) => (
  <p className={`font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500 ${className}`}>
    A licensed provider determines eligibility.
  </p>
);

const GoalReviewCta = ({ testid }) => (
  <a
    href="#peptides-lead"
    data-testid={testid}
    className="inline-flex items-center justify-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-7 py-4 transition-all duration-200 hover:translate-y-[-2px] hover:shadow-[0_10px_30px_rgba(157,4,35,0.38)]"
  >
    Book a Free Goal Review <ArrowRight size={15} />
  </a>
);

export default function PeptidesGlp1() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Peptides & GLP-1s", path: "/peptides-glp1-boston" },
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE.url}/peptides-glp1-boston#service`,
    name: "Provider-Guided Peptide & GLP-1 Program with Coached Training",
    serviceType: "Longevity and wellness coaching",
    description: QUICK_ANSWER,
    provider: { "@id": `${SITE.url}/#business` },
    areaServed: ["Boston", "West Roxbury", "Dedham", "Needham", "Newton", "Brookline"].map((n) => ({ "@type": "Place", name: `${n}, MA` })),
    url: `${SITE.url}/peptides-glp1-boston`,
  };

  return (
    <>
      <Seo
        title="Peptide & GLP-1 Programs in Boston | Boston Iron Grit"
        description="Provider-guided peptide and GLP-1 programs paired with coached strength training in West Roxbury. Labs, InBody scans and licensed medical partners. Free goal review."
        path="/peptides-glp1-boston"
        schemas={[speakableSchema("/peptides-glp1-boston"), breadcrumbSchema(crumbs), faqSchema(PEPTIDE_FAQS), serviceSchema]}
      />

      {/* HERO */}
      <section className="relative min-h-[75vh] flex items-center bg-[#0A0A0B] overflow-hidden" data-testid="peptides-hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG.senior})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/85 to-[#0A0A0B]/40" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
          <Reveal>
            <Crumbs items={crumbs} />
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-[#9D0423]">// West Roxbury · Boston · Provider-Guided</p>
            <h1 className="mt-4 font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight max-w-3xl">
              Peptides & GLP-1s,<br /><span className="text-[#9D0423]">Paired With Real Coaching.</span>
            </h1>
            <p className="mt-7 text-neutral-200 text-base lg:text-lg leading-relaxed max-w-2xl border-l-2 border-[#9D0423] pl-5">
              Medications and peptides are tools. The training, protein and daily habits around them are what protect
              muscle, bone and long-term health — and that's the part we coach, with{" "}
              <Link to="/live-big-365" className="text-white underline decoration-[#9D0423] underline-offset-4 hover:text-[#E56A79] transition-colors">Live B.I.G. 365</Link>{" "}
              behind every protocol.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <GoalReviewCta testid="peptides-cta-goal-review" />
              <BtnGhost to="/gold-standard" data-testid="peptides-cta-gold">See Gold Standard Programs</BtnGhost>
            </div>
            <EligibilityNote className="mt-4" />
          </Reveal>
        </div>
      </section>

      {/* QUICK ANSWER */}
      <section className="bg-[#0A0A0B] pt-10">
        <QuickAnswer>{QUICK_ANSWER}</QuickAnswer>
      </section>

      {/* WHY TRAIN WITH IT */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28" data-testid="peptides-why">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>The Coaching Difference</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              Why train with it,<br /><span className="text-neutral-600">not instead of it.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-[15px] leading-[1.85] max-w-xl">
              Medications and peptides are tools. What protects muscle, bone and long-term health is how you train and
              eat alongside them. That's the part we coach. Every member on a provider-guided protocol gets:
            </p>
            <ul className="mt-7 space-y-3.5">
              {[
                "Strength training scaled to their level, 2–4 days a week",
                "Protein and daily-habit coaching (our 8 Daily Habits)",
                "InBody scans to track lean mass vs. fat mass, not just scale weight",
                "Lab baselines and retests to review with the provider",
              ].map((li) => (
                <li key={li} className="flex items-start gap-3 text-[15px] text-neutral-200">
                  <CheckCircle2 size={17} className="text-[#9D0423] mt-0.5 shrink-0" /> {li}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120}>
            <img src={IMG.coach} alt="Coached strength training session at Boston Iron Grit in West Roxbury" className="w-full h-[440px] object-cover" loading="lazy" />
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28" data-testid="peptides-how">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight mb-12">
              Five steps,<br /><span className="text-neutral-600">provider-guided.</span>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-5 gap-px bg-white/5 border border-white/5">
            {STEPS.map((s, i) => (
              <Reveal key={s.t} delay={i * 70}>
                <div className="bg-[#0A0A0B] p-6 h-full hover:bg-[#121214] transition-colors">
                  <div className="font-heading font-extrabold text-4xl text-white/10">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="mt-3 font-heading font-bold uppercase text-base text-white tracking-tight leading-snug">{s.t}</h3>
                  <p className="mt-2 text-[12px] text-neutral-400 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
            <Link to="/schedule" data-testid="peptides-link-schedule" className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.16em] text-neutral-300 hover:text-white transition-colors">
              <CalendarDays size={14} className="text-[#9D0423]" /> See when coached classes run <ArrowRight size={13} />
            </Link>
            <Link to="/contact" data-testid="peptides-link-contact" className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.16em] text-neutral-300 hover:text-white transition-colors">
              <MessageCircle size={14} className="text-[#9D0423]" /> Questions first? Contact us <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28" data-testid="peptides-who">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Who It's For</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight mb-12">
              Built for real people,<br /><span className="text-neutral-600">not before-and-afters.</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHO.map((w, i) => (
              <Reveal key={w.t} delay={i * 80}>
                <div className="p-7 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors h-full">
                  <w.icon size={22} className="text-[#9D0423]" />
                  <h3 className="mt-5 font-heading font-bold uppercase text-lg text-white tracking-tight leading-snug">{w.t}</h3>
                  <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUDED VS BILLED SEPARATELY */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28" data-testid="peptides-table">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Costs, Plainly</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight mb-12">
              What's included vs.<br /><span className="text-neutral-600">billed separately.</span>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            <Reveal>
              <div className="bg-[#0A0A0B] p-8 h-full">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#E56A79]">
                  Included with <Link to="/live-big-365" className="underline underline-offset-2 hover:text-white transition-colors">Live B.I.G. 365</Link> — $100/mo, free with Platinum
                </p>
                <ul className="mt-6 space-y-3.5">
                  {["8 Daily Habits coaching", "InBody tracking", "Member pricing on labs and partner services", "Coach check-ins"].map((li) => (
                    <li key={li} className="flex items-start gap-3 text-[15px] text-neutral-200">
                      <CheckCircle2 size={17} className="text-[#9D0423] mt-0.5 shrink-0" /> {li}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={90}>
              <div className="bg-[#0A0A0B] p-8 h-full">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-400">Billed separately through licensed partners</p>
                <ul className="mt-6 space-y-3.5">
                  {["Provider consultations", "Lab panels", "Any prescribed peptides or GLP-1 medications"].map((li) => (
                    <li key={li} className="flex items-start gap-3 text-[15px] text-neutral-200">
                      <FlaskConical size={17} className="text-neutral-500 mt-0.5 shrink-0" /> {li}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SAFETY & MEDICAL OVERSIGHT */}
      <section className="bg-[#0A0A0B] py-16 lg:py-20" data-testid="peptides-safety">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <div className="p-8 lg:p-10 bg-[#121214] border-l-2 border-[#9D0423] border-y border-r border-y-white/10 border-r-white/10">
              <p className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.24em] text-[#E56A79]">
                <ShieldCheck size={15} /> Safety & Medical Oversight
              </p>
              <p className="mt-5 text-[15px] lg:text-base text-neutral-200 leading-[1.85]">
                Boston Iron Grit is a coaching gym, not a medical practice. Peptides, GLP-1 medications and lab panels
                are provided through licensed medical partners, who perform the consultation, eligibility screening and
                prescribing. Our coaching is not medical advice; always consult your physician about medical decisions.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BEST PLACE TO START */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28" data-testid="peptides-gold-standard">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Best Place To Start</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              Start with a<br /><span className="text-[#D4AF37]">Gold Standard Program.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-[15px] leading-[1.85] max-w-2xl">
              Gold Standard Programs include personal training and labs, and are the recommended entry point for anyone
              interested in peptides and GLP-1s — your baseline, provider consultation and coaching all live in one plan.
            </p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {[
              ["The Foundation", "$1,985", "3-month program — personal training, baseline labs and InBody, the 8 Daily Habits."],
              ["The Ascension", "$2,525", "3 months — more sessions, deeper testing and medical-partner protocol reviews."],
              ["The Elite Performance", "$6,800", "6 months of complete support with provider-guided longevity protocols."],
            ].map(([name, price, d], i) => (
              <Reveal key={name} delay={i * 90}>
                <div className={`p-7 h-full ${i === 1 ? "bg-[#141416] border-2 border-[#D4AF37]" : "bg-[#121214] border border-white/8"}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">{name}</p>
                  <div className="mt-3 font-heading font-extrabold text-4xl text-white">{price}</div>
                  <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
              <Link
                to="/gold-standard"
                data-testid="peptides-gold-link"
                className="inline-flex items-center justify-center gap-2 text-[#0A0A0B] font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-7 py-4 transition-all duration-200 hover:translate-y-[-2px] bg-[#D4AF37]"
              >
                See Gold Standard Programs <ArrowRight size={15} />
              </Link>
              <GoalReviewCta testid="peptides-cta-goal-review-2" />
            </div>
            <EligibilityNote className="mt-4" />
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <FaqList faqs={PEPTIDE_FAQS} title="Peptides & GLP-1s — FAQs" />
          </Reveal>
        </div>
      </section>

      {/* LEAD FORM */}
      <div id="peptides-lead" className="scroll-mt-24">
        <LeadBand
          formName="peptides-glp1"
          programInterest="Peptides / GLP-1s & Live B.I.G. 365"
          title="Book a free goal review."
          sub="A 20-minute conversation about your goals, history and health priorities — then labs, an InBody baseline and a provider consultation if it's the right fit. A licensed provider determines eligibility."
        />
      </div>
    </>
  );
}
